// Questionnaire definition (docs/maturity/02 "Instrument lifecycle", 04 "Catalog initialization"). A definition is a
// small versioned JSON tree: one dimension → subdimensions → categories → questions. Node ids are stable strings
// inside an immutable version, never SQL rows; reordering never renumbers them.
import { z } from "zod";
import type { FieldError } from "../api.ts";
import { requiredText } from "../setup.ts";

export const MATURITY_METHOD = "gp-halfpoints-equalmeans-v1";
export const MATURITY_LIMITS = { name: 200, code: 200, label: 200, nodeTitle: 1000, questionText: 4000, comment: 5000, questions: 1000 } as const;

export const nodeId = z
  .string({ error: "Identificador inválido." })
  .min(1, "Identificador inválido.")
  .max(100, "Identificador inválido.")
  .regex(/^[A-Za-z0-9][A-Za-z0-9_.:-]*$/, "Identificador inválido.");

// Traceability only (sheet, row, raw code); never used for authorization or scoring.
const source = z.record(z.string().max(100), z.union([z.string().max(500), z.number(), z.null()])).optional();
const code = requiredText(MATURITY_LIMITS.code, "Informe o código.");
const title = requiredText(MATURITY_LIMITS.nodeTitle, "Informe o título.");

export const questionSchema = z.strictObject({ id: nodeId, code, text: requiredText(MATURITY_LIMITS.questionText, "Informe o texto do quesito."), source });
export const categorySchema = z.strictObject({ id: nodeId, code, title, source, questions: z.array(questionSchema).max(MATURITY_LIMITS.questions) });
export const subdimensionSchema = z.strictObject({ id: nodeId, code, title, source, categories: z.array(categorySchema).max(MATURITY_LIMITS.questions) });
export const dimensionSchema = z.strictObject({ id: nodeId, code, title, source, subdimensions: z.array(subdimensionSchema).max(MATURITY_LIMITS.questions) });

export const definitionSchema = z
  .strictObject({
    schema_version: z.literal(1),
    dimension: dimensionSchema,
    provenance: z.record(z.string().max(100), z.string().max(2000)).optional(),
  })
  .superRefine((d, ctx) => {
    const qs = d.dimension.subdimensions.flatMap((s) => s.categories.flatMap((c) => c.questions));
    if (qs.length > MATURITY_LIMITS.questions) ctx.addIssue({ code: "custom", path: ["dimension"], message: `Use no máximo ${MATURITY_LIMITS.questions} quesitos.` });
    // Ids must be unique across the whole tree even in a draft: responses are keyed by them.
    const seen = new Set<string>();
    for (const [n, path] of walk(d)) {
      if (seen.has(n.id)) ctx.addIssue({ code: "custom", path: [...path, "id"], message: "Identificador repetido." });
      seen.add(n.id);
    }
  });

export type Definition = z.output<typeof definitionSchema>;
export type DefQuestion = z.output<typeof questionSchema>;
export type DefCategory = z.output<typeof categorySchema>;
export type DefSubdimension = z.output<typeof subdimensionSchema>;

type Node = { id: string; code: string };
function* walk(d: Pick<Definition, "dimension">): Generator<[Node, (string | number)[], "dimension" | "subdimension" | "category" | "question"]> {
  yield [d.dimension, ["dimension"], "dimension"];
  for (const [si, s] of d.dimension.subdimensions.entries()) {
    const sp = ["dimension", "subdimensions", si];
    yield [s, sp, "subdimension"];
    for (const [ci, c] of s.categories.entries()) {
      const cp = [...sp, "categories", ci];
      yield [c, cp, "category"];
      for (const [qi, q] of c.questions.entries()) yield [q, [...cp, "questions", qi], "question"];
    }
  }
}

export const questionsOf = (d: Pick<Definition, "dimension">) => d.dimension.subdimensions.flatMap((s) => s.categories.flatMap((c) => c.questions));

/** Publication rules: no empty branch, unique codes per level. Returns field errors (empty = publishable). */
export function publishErrors(d: Definition): FieldError[] {
  const errors: FieldError[] = [];
  const at = (path: (string | number)[]) => ["definition", ...path].join(".");
  if (!d.dimension.subdimensions.length) errors.push({ path: at(["dimension", "subdimensions"]), message: "Inclua pelo menos uma subdimensão." });
  d.dimension.subdimensions.forEach((s, si) => {
    if (!s.categories.length) errors.push({ path: at(["dimension", "subdimensions", si, "categories"]), message: `Inclua pelo menos uma categoria em ${s.code}.` });
    s.categories.forEach((c, ci) => {
      if (!c.questions.length) errors.push({ path: at(["dimension", "subdimensions", si, "categories", ci, "questions"]), message: `Inclua pelo menos um quesito em ${c.code}.` });
    });
  });
  const codes = new Map<string, Set<string>>();
  for (const [n, path, level] of walk(d)) {
    const set = codes.get(level) ?? new Set<string>();
    const key = n.code.trim().toLowerCase();
    if (set.has(key)) errors.push({ path: at([...path, "code"]), message: `Código ${n.code} repetido.` });
    set.add(key);
    codes.set(level, set);
  }
  return errors;
}

/** New stable node id for a manually added node; never derived from position. */
export const newNodeId = (prefix: "dim" | "sub" | "cat" | "q") => `${prefix}-${crypto.randomUUID().slice(0, 8)}`;
